import React from 'react';
import { motion } from 'framer-motion';
import { Apple, Play, ArrowDown } from 'lucide-react';
import Coin from './Coin';

const APP_STORE =
  'https://apps.apple.com/in/app/money-falls/id6755540069';
const PLAY_STORE =
  'https://play.google.com/store/apps/details?id=com.moneyfalls.app';

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background flourishes */}
      <div className="absolute inset-0 -z-10 bg-grid-red [background-size:48px_48px] opacity-40" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand/20 blur-[140px] -z-10" />
      <div className="absolute top-60 -left-40 w-[420px] h-[420px] rounded-full bg-brand-accent/15 blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        {/* Copy */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-brand/20 bg-brand/5 text-brand"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            <span className="font-pixel text-[11px] tracking-[0.2em] uppercase">
              Now Live · v1.0
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-7 text-[44px] sm:text-6xl md:text-7xl lg:text-[78px] leading-[0.96] font-semibold tracking-tightest"
            data-testid="hero-heading"
          >
            Turn waiting
            <br />
            into <span className="relative inline-block">
              <span className="text-brand">earning</span>
              <svg
                viewBox="0 0 300 18"
                className="absolute -bottom-2 left-0 w-full"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 10 Q 75 0, 150 8 T 298 8"
                  stroke="#E50914"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>.
            <br />
            <span className="text-ink/60 text-[38px] sm:text-5xl md:text-6xl lg:text-[64px]">
              Fun like never before.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-7 max-w-xl text-[17px] md:text-lg text-ink/60 leading-relaxed"
            data-testid="hero-description"
          >
            CoinsFlu is an offerwall-based earning app where users get rewarded
            while enjoying tasks, games, and experiences. We've made waiting
            fun — something no one has done before.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-9 flex flex-col sm:flex-row gap-3.5"
          >
            <a
              href={APP_STORE}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-app-store-btn"
              className="group inline-flex items-center gap-3 bg-ink text-white px-6 py-4 rounded-2xl hover:bg-black transition-all active:scale-[0.98]"
            >
              <Apple size={26} strokeWidth={1.5} />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-white/60">
                  Download on the
                </div>
                <div className="text-base font-semibold">App Store</div>
              </div>
            </a>

            <a
              href={PLAY_STORE}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-play-store-btn"
              className="group inline-flex items-center gap-3 bg-brand hover:bg-brand-hover text-white px-6 py-4 rounded-2xl btn-primary transition-all active:scale-[0.98]"
            >
              <Play size={22} fill="white" strokeWidth={0} />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-white/70">
                  Get it on
                </div>
                <div className="text-base font-semibold">Google Play</div>
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-10 flex items-center gap-5 text-ink/50 text-sm"
            data-testid="hero-trust"
          >
            <div className="flex -space-x-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-brand to-brand-accent"
                  style={{ filter: `hue-rotate(${i * 14}deg)` }}
                />
              ))}
            </div>
            <span className="max-w-xs">
              Joined by early earners across India — every tap pays.
            </span>
          </motion.div>
        </div>

        {/* Phone Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: -10 }}
          animate={{ opacity: 1, y: 0, rotate: -6 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="lg:col-span-5 relative flex justify-center lg:justify-end"
        >
          <PhoneMockup />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <a
        href="#about"
        data-testid="hero-scroll-cue"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-ink/40 hover:text-brand transition-colors"
      >
        <span className="font-pixel text-[10px] tracking-[0.2em] uppercase">
          Scroll
        </span>
        <ArrowDown size={14} className="animate-bounce" />
      </a>
    </section>
  );
}

function PhoneMockup() {
  return (
    <div className="relative animate-float">
      {/* Glow halo */}
      <div className="absolute inset-0 -m-12 bg-brand/30 rounded-[60px] blur-3xl -z-10" />

      {/* Phone frame */}
      <div className="relative w-[290px] sm:w-[320px] md:w-[340px] aspect-[9/19] rounded-[48px] bg-ink p-3 shadow-[0_40px_80px_-20px_rgba(229,9,20,0.45)] border border-black/60">
        {/* Notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 rounded-full bg-black z-20" />

        {/* Screen */}
        <div className="relative w-full h-full rounded-[38px] overflow-hidden bg-brand flex flex-col items-center justify-between py-10 px-5">
          {/* Bg pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
              backgroundSize: '14px 14px',
            }}
          />

          {/* Top: status + balance */}
          <div className="relative w-full">
            <div className="flex items-center justify-between text-white/70 text-[10px] font-pixel tracking-widest">
              <span>9:41</span>
              <span>●●● ▮</span>
            </div>

            <div className="mt-6 text-center">
              <div className="font-pixel text-white/70 text-[10px] tracking-[0.3em] uppercase">
                Your Balance
              </div>
              <div className="mt-2 flex items-center justify-center gap-2 text-white text-5xl font-bold tracking-tight">
                <Coin size={38} />
                <span>1,247</span>
              </div>
              <div className="mt-1 flex items-center justify-center gap-1.5 text-white/60 text-xs">
                <Coin size={12} />
                <span>+42 today</span>
              </div>
            </div>
          </div>

          {/* Middle: Coin icon */}
          <div className="relative">
            <div className="w-32 h-32 rounded-full bg-white/95 flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.25)] relative">
              <div className="absolute inset-2 rounded-full border-[3px] border-dashed border-brand/40 animate-spin [animation-duration:12s]" />
              <Coin size={72} />
            </div>
            <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg gap-0.5">
              <Coin size={12} />
              <span className="font-pixel text-brand text-[10px]">+5</span>
            </div>
            <div className="absolute -bottom-2 -left-4 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow-md gap-0.5">
              <Coin size={10} />
              <span className="font-pixel text-brand text-[9px]">+1</span>
            </div>
          </div>

          {/* Bottom: tasks */}
          <div className="relative w-full space-y-2">
            {[
              { t: 'Play & Earn', r: '+12' },
              { t: 'Watch Ad', r: '+3' },
              { t: 'Daily Streak', r: '+7' },
            ].map((row, i) => (
              <div
                key={i}
                className="flex items-center justify-between bg-white/15 backdrop-blur-md rounded-xl px-3 py-2 border border-white/20"
              >
                <span className="text-white text-[13px] font-medium">
                  {row.t}
                </span>
                <span className="inline-flex items-center gap-1 font-pixel text-white text-[11px] tracking-wider">
                  <Coin size={12} />
                  <span>{row.r}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating badges */}
      <div className="absolute -left-6 top-24 glass rounded-2xl px-4 py-3 shadow-glass animate-float [animation-delay:-2s] hidden sm:block">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center">
            <Coin size={18} />
          </div>
          <div>
            <div className="text-[10px] text-ink/50 uppercase tracking-wider font-pixel">
              Earned
            </div>
            <div className="text-sm font-semibold text-ink">in 12 secs</div>
          </div>
        </div>
      </div>

      <div className="absolute -right-4 bottom-28 glass rounded-2xl px-4 py-3 shadow-glass animate-float [animation-delay:-4s] hidden sm:block">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
            <Play size={14} fill="white" strokeWidth={0} />
          </div>
          <div>
            <div className="text-[10px] text-ink/50 uppercase tracking-wider font-pixel">
              Instant
            </div>
            <div className="text-sm font-semibold text-ink">Rewards</div>
          </div>
        </div>
      </div>
    </div>
  );
}
